import { useCallback, useEffect, useRef, useState } from "react";
import { useAction, useMutation } from "convex/react";
import { api } from "@convex/_generated/api";
import type { Id } from "@convex/_generated/dataModel";
import { CHUNK_INTERVAL_MS } from "@protocol";
import { CloudUpload, FileAudio, Loader2, TriangleAlert } from "lucide-react";
import { primaryButtonClass, secondaryButtonClass } from "@/lib/ui";
import {
  clearRecording,
  listSessions,
  pendingBytes,
  pendingSeqs,
  type RecorderSession,
  type UploadedFileInfo,
} from "./chunkStore";
import {
  ChunkUploader,
  FILE_UPLOAD_WINDOW,
  FileChunkSource,
  type UploaderStatus,
} from "./uploader";
import { INGEST_WS_URL, useRecorder } from "./context";
import { AUDIO_ACCEPT, formatBytes } from "./audioFile";

/**
 * Crash recovery, PLAN.md §7 "Recovery".
 *
 * If the tab died mid-lecture the audio is still in IndexedDB - nothing else
 * knows about it, so the app has to look. On load every unfinalized session is
 * listed with what it still owes, and the user can drain it or throw it away.
 *
 * The capture itself cannot continue (the microphone stream is gone), so
 * resuming means: upload the remainder, then finalize.
 *
 * A dropped file never had its bytes copied here, only its name and size, so
 * resuming one asks for the file again. The server's resume handshake then
 * skips every slice it already holds.
 */

/** Same size, and the same name or timestamp - a renamed copy still counts. */
function sameFile(file: File, info: UploadedFileInfo): boolean {
  return (
    file.size === info.fileBytes &&
    (file.name === info.fileName || file.lastModified === info.lastModified)
  );
}

interface Recoverable {
  session: RecorderSession;
  pending: number;
  bytes: number;
}

type ItemState =
  | { kind: "idle" }
  | { kind: "uploading"; connection: UploaderStatus; pending: number }
  | { kind: "failed"; message: string };

export default function RecoveryBanner() {
  const recorder = useRecorder();
  const markStopped = useMutation(api.recordings.markStopped);
  const markFailed = useMutation(api.recordings.markFailed);
  const removeRecording = useMutation(api.recordings.remove);
  const issueToken = useAction(api.recordings.issueIngestToken);

  const [items, setItems] = useState<Recoverable[]>([]);
  const [states, setStates] = useState<Record<string, ItemState>>({});
  const uploaders = useRef(new Map<string, ChunkUploader>());
  const activeId = recorder.recordingId;

  const patchState = useCallback((id: string, state: ItemState) => {
    setStates((current) => ({ ...current, [id]: state }));
  }, []);

  const drop = useCallback((id: string) => {
    uploaders.current.get(id)?.dispose();
    uploaders.current.delete(id);
    setItems((current) => current.filter((item) => item.session.recordingId !== id));
  }, []);

  // Scan once, on load. A session started in *this* tab is the live recorder's
  // business, not the banner's.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      let sessions: RecorderSession[];
      try {
        sessions = await listSessions();
      } catch {
        return; // No IndexedDB (private mode) - nothing to recover from.
      }
      const found: Recoverable[] = [];
      for (const session of sessions) {
        if (session.finalized) {
          await clearRecording(session.recordingId).catch(() => undefined);
          continue;
        }
        const pending = (await pendingSeqs(session.recordingId)).length;
        if (pending === 0 && session.nextSeq === 0) {
          // Started and died before the first blob: nothing to offer.
          await clearRecording(session.recordingId).catch(() => undefined);
          continue;
        }
        found.push({
          session,
          pending,
          bytes: await pendingBytes(session.recordingId),
        });
      }
      if (!cancelled) setItems(found);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Unmounting the banner must not leave sockets running.
  useEffect(() => {
    const running = uploaders.current;
    return () => {
      for (const uploader of running.values()) uploader.dispose();
      running.clear();
    };
  }, []);

  const resume = useCallback(
    async (item: Recoverable, file?: File) => {
      const id = item.session.recordingId;
      if (uploaders.current.has(id)) return;
      if (INGEST_WS_URL === "") {
        patchState(id, { kind: "failed", message: "VITE_INGEST_WS_URL is not configured." });
        return;
      }

      const info = item.session.upload;
      let source: FileChunkSource | undefined;
      if (info !== undefined) {
        if (file === undefined) return;
        if (!sameFile(file, info)) {
          patchState(id, {
            kind: "failed",
            message: `That is not the same file - expected ${info.fileName} (${formatBytes(info.fileBytes)}).`,
          });
          return;
        }
        source = new FileChunkSource(file, info.chunkBytes);
      }
      patchState(id, {
        kind: "uploading",
        connection: "connecting",
        pending: source?.chunkCount ?? item.pending,
      });

      // The capture is over either way; move the row out of `recording` so the
      // state machine matches reality even if the upload takes a while. An
      // upload never was in `recording`.
      if (info === undefined) {
        await markStopped({
          id: id as Id<"recordings">,
          durationMs: item.session.nextSeq * CHUNK_INTERVAL_MS,
        }).catch(() => undefined);
      }

      const uploader = new ChunkUploader({
        recordingId: id,
        mimeType: item.session.mimeType,
        wsUrl: INGEST_WS_URL,
        getToken: async () => (await issueToken({ id: id as Id<"recordings"> })).token,
        source,
        maxInFlight: source === undefined ? undefined : FILE_UPLOAD_WINDOW,
        onStatus: (connection) =>
          setStates((current) => {
            const existing = current[id];
            if (existing?.kind !== "uploading") return current;
            return { ...current, [id]: { ...existing, connection } };
          }),
        onPending: (pending) =>
          setStates((current) => {
            const existing = current[id];
            if (existing?.kind !== "uploading") return current;
            return { ...current, [id]: { ...existing, pending } };
          }),
        onFinalized: () => {
          void clearRecording(id).catch(() => undefined);
          drop(id);
        },
        onFatal: (message) => patchState(id, { kind: "failed", message }),
      });
      uploaders.current.set(id, uploader);
      uploader.start();
      uploader.requestFinalize();
    },
    [drop, issueToken, markStopped, patchState],
  );

  const discard = useCallback(
    async (item: Recoverable) => {
      const id = item.session.recordingId;
      drop(id);
      await clearRecording(id).catch(() => undefined);
      if (item.session.upload !== undefined) {
        // A half-sent file is not worth keeping a row for; the slices already
        // on the server are swept as an orphan directory.
        await removeRecording({ id: id as Id<"recordings"> }).catch(() => undefined);
        return;
      }
      await markFailed({
        id: id as Id<"recordings">,
        error: "Unfinished recording discarded from the recovery banner.",
      }).catch(() => undefined);
    },
    [drop, markFailed, removeRecording],
  );

  const visible = items.filter((item) => item.session.recordingId !== activeId);
  if (visible.length === 0) return null;

  return (
    <div className="flex flex-col gap-2">
      {visible.map((item) => {
        const id = item.session.recordingId;
        const state = states[id] ?? { kind: "idle" };
        const info = item.session.upload;
        return (
          <div
            key={id}
            className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-xl border border-[#F59E0B]/40 bg-[#F59E0B]/10 px-4 py-3 text-sm"
          >
            {info !== undefined ? (
              <FileAudio className="h-4 w-4 shrink-0 text-[#F59E0B]" />
            ) : (
              <TriangleAlert className="h-4 w-4 shrink-0 text-[#F59E0B]" />
            )}
            <span className="min-w-0 flex-1 text-[#222] dark:text-[#ededeb]">
              {info !== undefined ? (
                <>
                  Upload of <span className="font-medium break-all">{info.fileName}</span> (
                  {formatBytes(info.fileBytes)}) was interrupted. Pick the file again to finish it.
                </>
              ) : (
                <>
                  Unfinished recording from {startLabel(item.session.startedAt)},{" "}
                  {approxMinutes(item.session.nextSeq)} min,{" "}
                  {item.pending} chunk{item.pending === 1 ? "" : "s"} pending upload
                  {item.bytes > 0 && ` (${formatBytes(item.bytes)})`}
                </>
              )}
            </span>

            {state.kind === "uploading" ? (
              <span className="flex items-center gap-2 font-mono text-xs text-neutral-600 dark:text-neutral-400">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                {state.connection === "online" || state.connection === "finalizing"
                  ? info !== undefined
                    ? `${Math.floor((1 - state.pending / Math.max(1, item.session.nextSeq)) * 100)}%`
                    : `${state.pending} left`
                  : state.connection === "connecting"
                    ? "connecting…"
                    : "offline - retrying"}
              </span>
            ) : (
              <div className="flex items-center gap-2">
                {info !== undefined ? (
                  <label className={primaryButtonClass}>
                    <input
                      type="file"
                      accept={AUDIO_ACCEPT}
                      className="hidden"
                      onChange={(event) => {
                        const file = event.target.files?.[0];
                        event.target.value = "";
                        if (file !== undefined) void resume(item, file);
                      }}
                    />
                    <span className="flex items-center gap-1.5">
                      <CloudUpload className="h-3.5 w-3.5" />
                      Choose file to resume
                    </span>
                  </label>
                ) : (
                  <button
                    type="button"
                    className={primaryButtonClass}
                    onClick={() => void resume(item)}
                  >
                    <span className="flex items-center gap-1.5">
                      <CloudUpload className="h-3.5 w-3.5" />
                      Resume upload
                    </span>
                  </button>
                )}
                <button
                  type="button"
                  className={secondaryButtonClass}
                  onClick={() => void discard(item)}
                >
                  Discard
                </button>
              </div>
            )}

            {state.kind === "failed" && (
              <span className="w-full text-xs text-red-600 dark:text-red-400">
                {state.message}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}

/** "Mon 14:05" - enough to recognise which lecture it was. */
function startLabel(startedAt: number): string {
  return new Date(startedAt).toLocaleString(undefined, {
    weekday: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** The exact duration died with the tab; seq count is the best estimate left. */
function approxMinutes(nextSeq: number): number {
  return Math.max(1, Math.round((nextSeq * CHUNK_INTERVAL_MS) / 60_000));
}

